import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { getFormattedSsn } from '@sputnik/components/utils'
import AddPersonDrawer, {
  type AddPersonDrawerOpenable,
} from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddPersonDrawer'
import { useGetPeopleInfoDataLazyQuery } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/GetPeopleInfoData.gql'
import { useUpdatePeopleInfoData } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/UpdatePeopleInfoData.gql'
import { AddPersonForm } from '@sputnik/schema/creditApplication/addPerson'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'
import { epochToDate } from '@sputnik/utils/mutationUtils'

export interface UpdateCompanyOwnerDrawerProps extends AddPersonDrawerOpenable {
  personId: string
}

const UpdateCompanyOwnerDrawer = ({ isShowing, onClose, personId }: UpdateCompanyOwnerDrawerProps) => {
  const { appId } = useParams()
  const showToast = useToast()
  const [authorizedSignerName, setAuthorizedSignerName] = useState('')
  const { tracker } = useWebTracking()

  const [getCompanyOwners] = useGetPeopleInfoDataLazyQuery({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const [updateCompanyOwner] = useUpdatePeopleInfoData({
    onError: (error) => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Unable to update company owner'
          id='sputnik.UpdateCompanyOwnerDrawer__QTnwzz'
        />,
        {
          className: 'fs-unmask',
          autoHideDelay: 5000,
        },
      )
      logError(error, {
        attributes: {
          action: 'updateCompanyOwner',
          creditAppId: appId,
          result: error,
          message: 'Error while updating company owner.',
        },
        eventName: 'updateCompanyOwner',
      })
    },
  })

  const initialValues = async (): Promise<AddPersonForm> => {
    setAuthorizedSignerName('')
    const { data } = await getCompanyOwners()

    const { beneficialOwners, authorizedSigner } = data?.creditApplication ?? {}

    const beneficialOwner = beneficialOwners?.find((owner) => owner && owner.id === personId)

    const {
      address,
      dob,
      email,
      firstName,
      lastName,
      ssnLastFour,
      passportCountry,
      passportExpiration,
      passportNumberLength,
      passportNumberLastFour,
      phoneNumber,
      title,
    } = beneficialOwner ?? {}

    if (email === authorizedSigner?.email) {
      setAuthorizedSignerName(`${firstName} ${lastName}`)
    }

    const { city, countryCode, postalCode, state, street, streetAdditional } = address ?? {}

    return {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      phoneNumber: phoneNumber ?? '',
      title: title ?? '',
      dob: dob ?? '',
      ssn: {
        value: '',
        display: ssnLastFour ? getFormattedSsn(`*****${ssnLastFour}`, true) : '',
      },
      passportCountry: passportCountry ?? undefined,
      passportExpiration: passportExpiration ?? undefined,
      passportNumber: {
        value: '',
        display: passportNumberLastFour
          ? `${new Array((passportNumberLength ?? 9) - 4).fill('*').join('')}${passportNumberLastFour}`
          : undefined,
      },
      isNonUSCitizen: !!passportCountry || !!passportExpiration || !!passportNumberLastFour,
      isForeignAddress: countryCode && countryCode !== 'US' ? 'YES' : 'NO',
      street: street ?? '',
      streetAdditional: streetAdditional ?? '',
      city: city ?? '',
      state: state ?? '',
      postalCode: postalCode ?? '',
      countryCode: countryCode ? countryCode : 'US',
    }
  }

  const onSubmit = async (formValues: AddPersonForm) => {
    tracker?.track('se_signup__edit_person_drawer_saved__fired', {
      person_type: 'companyOwner',
    })
    await updateCompanyOwner({
      variables: {
        beneficialOwners: [
          {
            id: personId,
            dob: epochToDate(formValues.dob) || '',
            email: formValues.email || '',
            firstName: formValues.firstName || '',
            lastName: formValues.lastName || '',
            phoneNumber: formValues.phoneNumber || '',
            ...(formValues.ssn?.value && {
              ssn: formValues.ssn?.value,
              passportNumber: null,
              passportCountry: null,
              passportExpiration: null,
            }),
            ...(formValues.title && {
              title: formValues.title,
            }),
            ...(formValues.passportNumber?.value &&
              formValues.passportExpiration &&
              formValues.passportCountry && {
                passportNumber: formValues.passportNumber?.value,
                passportCountry: formValues.passportCountry,
                passportExpiration: formValues.passportExpiration,
                ssn: null,
              }),
            address: {
              street: formValues.street || '',
              streetAdditional: formValues.streetAdditional || '',
              city: formValues.city || '',
              state: formValues.state || '',
              postalCode: formValues.postalCode || '',
              countryCode: formValues.countryCode || 'US',
            },
          },
        ],
        creditApplicationId: appId ?? '',
      },
      onCompleted: () => {
        logInfo('updateCompanyOwner', {
          attributes: {
            creditAppId: appId,
            result: `company owner updated successfully`,
          },
          eventName: 'updateCompanyOwner',
        })
        showToast(
          TOAST_TYPE_SUCCESS,
          <FormattedMessage
            defaultMessage='Company owner updated'
            id='sputnik.UpdateCompanyOwnerDrawer__szV1Y1'
          />,
          {
            autoHideDelay: 5000,
          },
        )
        onClose()
      },
    })
  }

  return (
    isShowing && (
      <AddPersonDrawer
        initialValues={initialValues}
        isShowing={isShowing}
        title={
          <FormattedMessage
            defaultMessage='Update company owner'
            id='sputnik.UpdateCompanyOwnerDrawer__1gHvKW'
          />
        }
        onClose={() => {
          tracker?.track('se_signup__edit_person_drawer_cancelled__fired', {
            person_type: 'companyOwner',
          })
          onClose()
        }}
        onSubmit={onSubmit}
        {...(authorizedSignerName && {
          banner: (
            <FormattedMessage
              defaultMessage='{name} is the authorized signer. Information changed in this form will be reflected in the authorized signer form.'
              id='sputnik.UpdateCompanyOwnerDrawer__LpxMdy'
              values={{
                name: authorizedSignerName,
              }}
            />
          ),
          isAuthorizedSigner: true,
        })}
      />
    )
  )
}

export default UpdateCompanyOwnerDrawer
